import { useQuery, QueryClient } from '@tanstack/react-query';
import { getTaskQueueCounts, TaskQueueCounts } from '../../services/taskQueueService';

const EMPTY_COUNTS: TaskQueueCounts = {
  all: 0,
  byDepartment: { billing: 0, clinical: 0, logistics: 0, blister_pack: 0, psp: 0, inventory: 0, none: 0 },
  mine: 0,
};

export function taskQueueCountsQueryKey(pharmacyId: string | undefined) {
  return ['task-queue-counts', pharmacyId] as const;
}

/**
 * PRX-67 (R7) — open-task counts (all / per department / mine) backing the
 * Task Queue's tab strip and the sidebar's Task Queue badge (Task 3). Modelled
 * on `useConversationCounts`: one query, keyed on pharmacy, 30s stale time so
 * the tabs don't hammer the endpoint on every render but still pick up a
 * mutation elsewhere within half a minute even without an explicit invalidate.
 */
export function useTaskQueueCounts(pharmacyId: string | undefined) {
  return useQuery({
    queryKey: taskQueueCountsQueryKey(pharmacyId),
    queryFn: async (): Promise<TaskQueueCounts> => {
      if (!pharmacyId) return EMPTY_COUNTS; // Extra safeguard
      return getTaskQueueCounts(pharmacyId);
    },
    enabled: !!pharmacyId,
    staleTime: 30_000,
  });
}

/**
 * Called after a task is created, completed/acknowledged, or (re)assigned —
 * any mutation that can move a task across a department/mine/all bucket — so
 * the tab chips and the sidebar badge don't wait out the 30s stale time.
 */
export function invalidateTaskQueueCounts(queryClient: QueryClient, pharmacyId: string | undefined) {
  return queryClient.invalidateQueries({ queryKey: taskQueueCountsQueryKey(pharmacyId) });
}
